
import AppVatSelect from "@/components/SelectVat.vue";
import { Product } from "@/models/base";
import { Vat } from "@/models/base";
import { TaxesConfig } from "@/models/dossier-configs/taxes-config";
import { UserConfigService } from "@/services";
import { DossierConfigService } from "@/services/dossier-config-service";
import { SellCategory } from "@/utils/sell-category";

import Vue from "vue";

export default Vue.extend({
	data() {
		return {
			pricePerUnit: 0.0,
			currentFocus: "",
			currentPercentage: 21,
			computedValues: {
				bruto: 0,
				profit: 0,
				profitPercentage: 0
			},
			DossierConfigService
		};
	},
	props: {
		priceCategory: {
			type: Number,
			required: true
		},
		product: Object as () => Product,
	},
	computed: {
		taxesConfig():TaxesConfig{
			return this.DossierConfigService.getTaxesConfig();
		},
		totalTaxes():number{
			return this.product.RecupelTax + this.product.AuvibelTax + this.product.BebatTax + this.product.ReprobelTax + this.product.LeeggoedTax;
		},
		staticProfitPercentageSet():boolean {
			if (!this.product.ProfitRules) return false;
			return this.product.ProfitRules.getFixedPercentageOfCategory(this.priceCategory) !== null;
		},
		staticProfitValueSet():boolean{
			if (!this.product.ProfitRules) return false;
			return this.product.ProfitRules.getFixedValueOfCategory(this.priceCategory) !== null;
		},
		staticProfitSet():boolean {
			return this.staticProfitPercentageSet || this.staticProfitValueSet;
		},
		config():SellCategory {
			return DossierConfigService.getSellCategories().getCategories()[this.priceCategory - 1];
		},
		unitPriceDisabled():boolean {
			return this.config.CalculateViaParameters || this.staticProfitSet;
		},
		brutoPriceDisabled():boolean{
			return this.config.CalculateViaParameters || this.staticProfitSet;
		},
		profitDisabled():boolean {
			return this.config.CalculateViaParameters || this.staticProfitPercentageSet;
		},
		profitPercentDisabled():boolean {
			return this.config.CalculateViaParameters || this.staticProfitValueSet;
		},
		decimals():number{
			return this.DossierConfigService.getDecimalSettings().Unit;
		},
		canSeeProfit():boolean {
			return UserConfigService.hasCurrentUserPermission(p=>p.CanReadProductPurchaseDetails);
		}
	},
	watch: {
		config: {
			immediate: true,
			async handler(config:SellCategory):Promise<void> {
				if (config.CalculateViaParameters) {
					let supplier = this.product.getMainSupplier();
					if (!supplier) {
						if(this.product.BuyNettoPrice!==null){
							let percentage = config.calculateProfitBasedOnParameters(this.product.BuyNettoPrice);
							await this.$nextTick();
							this.pricePerUnit = (this.product.BuyNettoPrice) * (1 + (percentage / 100.0));
						}else{
							this.pricePerUnit = 0;
							this.computedValues.profitPercentage = 0.0;
						}
					} else {
						let percentage = config.calculateProfitBasedOnParameters((supplier.NettoPrice*supplier.CurrencyRate));
						await this.$nextTick();
						this.pricePerUnit = (supplier.NettoPrice*supplier.CurrencyRate) * (1 + (percentage / 100.0));
					}
				}
			}
		},
		priceCategory: {
			immediate: true,
			handler() {
				this.updateEverything();
			}
		},
		product: {
			immediate: true,
			handler() {
				this.updateEverything();
			}
		},
		"taxesConfig.ProductEditPriceIncVatIncludesTaxes"(){
			this.computeBruto();
		},
		pricePerUnit(newValue: number, oldValue: number) {
			(this.product["SellPrice" + this.priceCategory as keyof Product] as number) = newValue;
			if (this.currentFocus != "price/unit") return;
			this.computeBruto();
			this.computeProfit();
			this.computeProfitPercentage();
		},
		"computedValues.bruto"(value: number) {
			if (this.currentFocus != "bruto") return;
			if (this.taxesConfig.ProductEditPriceIncVatIncludesTaxes){
				value -= this.totalTaxes;
			}
			this.pricePerUnit = value / ((100 + this.currentPercentage) / 100.0);
			this.computeProfit();
			this.computeProfitPercentage();
		},
		"computedValues.profit"(value: number) {
			if (this.currentFocus != "profit") return;
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier){
				mainSupplier = this.product.Suppliers.find(s=>s.IsMainSupplier == true) || null;
			};
			if (!mainSupplier){
				return;
			}
			this.pricePerUnit = (mainSupplier.NettoPrice * mainSupplier.CurrencyRate) + value;
			this.computeProfitPercentage();
			this.computeBruto();
			if (this.staticProfitValueSet && this.product.ProfitRules){
				this.product.ProfitRules.setFixedValueOfCategory(this.priceCategory, this.computedValues.profit);
			}
		},
		"computedValues.profitPercentage"(value: number) {
			if (this.currentFocus != "profitPercent") return;
			let mainSupplier = this.product.getMainSupplier();

			if (!mainSupplier?.IsMainSupplier){
				if(this.product?.BuyNettoPrice == null || this.product?.BuyNettoPrice==0)
					return;
				this.pricePerUnit = (this.product.BuyNettoPrice) * ((value + 100) / 100.0);
			}else{
				this.pricePerUnit = (mainSupplier.NettoPrice * mainSupplier.CurrencyRate) * ((value + 100) / 100.0);
			}
			this.computeProfit();
			this.computeBruto();
			this.computeProfit();
			if (this.staticProfitPercentageSet && this.product.ProfitRules){
				this.product.ProfitRules.setFixedPercentageOfCategory(this.priceCategory, this.computedValues.profitPercentage);
			}
		},
		currentPercentage() {
			this.computeBruto();
		},
		"product.Vat": {
			immediate: true,
			handler(vat: Vat) {
				if (!vat) return;
				this.product.VatID = vat.ID;
				this.currentPercentage = vat.Value;
				this.computeBruto();
			}
		},
		"product.BuyPriceComesFromComposition"(){
			this.computeProfit();
		}
	},
	methods: {
		updateEverything() {
			let newValue = this.product["SellPrice" + this.priceCategory as keyof Product] as number;
			if (newValue == this.pricePerUnit) return;
			this.pricePerUnit = newValue;
			this.computeBruto();
			this.computeProfit();
			this.computeProfitPercentage();
		},
		computeBruto() {
			let value = this.pricePerUnit * (1.0 + (this.currentPercentage / 100.0));
			if (this.taxesConfig.ProductEditPriceIncVatIncludesTaxes){
				value += this.totalTaxes;
			}
			this.computedValues.bruto = value;
		},
		computeProfit() {
			if (this.product.BuyPriceComesFromComposition) {
				this.computedValues.profit = this.pricePerUnit - this.product.getBuyPriceFromComposition();
				return;
			}
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier) {
				this.computedValues.profit = this.pricePerUnit;
			} else {
				this.computedValues.profit = this.pricePerUnit - ( mainSupplier.NettoPrice * mainSupplier.CurrencyRate);
			}
		},
		computeProfitPercentage() {
			if (this.product.BuyPriceComesFromComposition) {
				let buyPrice = this.product.getBuyPriceFromComposition();
				this.computedValues.profit = ((this.pricePerUnit - buyPrice));
				this.computedValues.profitPercentage =  buyPrice ? ((this.pricePerUnit - buyPrice) / buyPrice) * 100.0 : 0;
				return;
			}
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier) {
				this.computedValues.profitPercentage = 100;
			} else {

				/*if(this.staticProfitSet){
					this.computedValues.profitPercentage =  this.product.ProfitRules?.getFixedPercentageOfCategory(this.priceCategory) || 100;
					return;
				}*/

				let supPrice = (mainSupplier.NettoPrice * mainSupplier.CurrencyRate);
				this.computedValues.profitPercentage =  supPrice ? ((this.pricePerUnit - supPrice) / supPrice) * 100.0 : 0;
			}
		}
	},
	components: {
		AppVatSelect,
	},
	i18nEx: {
		componentPrefix: "components.products"
	}
});
